import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion'
import {
  AfterContentInit,
  Attribute,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { MatButton, MatIconButton } from '@angular/material/button'
import { Observable, Subscription, merge, of as observableOf } from 'rxjs'
import { NgxMatDatepickerControl, NgxMatDatepickerPanel } from '@ngxmc/datetime-picker'
import { MatIconButtonSizesModule } from 'mat-icon-button-sizes'

/** Can be used to override the icon of a `matDatepickerToggle`. */
@Directive({
  selector: '[ngxMatDatepickerToggleIcon]',
})
export class NgxMatDatepickerToggleIcon {}

/**
 * A copy of ngx-mat-datepicker-toggle to reduce the size of the icon to make the form fields align by height.
 * Source: https://github.com/GNURub/angular-material-components/blob/main/projects/datetime-picker/src/lib/datepicker-toggle.ts
 */
@Component({
  standalone: true,
  selector: 'app-mat-datepicker-toggle',
  templateUrl: './datetime-picker-toggle.component.html',
  styleUrls: ['./datetime-picker-toggle.component.scss'],
  host: {
    class: 'mat-datepicker-toggle',
    '[attr.tabindex]': 'null',
    '[class.mat-datepicker-toggle-active]': 'datepicker && datepicker.opened',
    '[class.mat-accent]': 'datepicker && datepicker.color === "accent"',
    '[class.mat-warn]': 'datepicker && datepicker.color === "warn"',
    '[attr.data-mat-calendar]': 'datepicker ? datepicker.id : null',
    '(click)': '_open($event)',
  },
  exportAs: 'appMatDatepickerToggle',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconButton, MatIconButtonSizesModule],
})
export class AppMatDatepickerToggle<D> implements AfterContentInit, OnChanges, OnDestroy {
  private _stateChanges = Subscription.EMPTY

  /** Datepicker instance that the button will toggle. */
  @Input('for')
  datepicker: NgxMatDatepickerPanel<NgxMatDatepickerControl<any>, D> | undefined

  /** Tabindex for the toggle. */
  @Input()
  tabIndex: number | null = null

  /** Screen-reader label for the button. */
  @Input('aria-label')
  ariaLabel: string | undefined

  /** Whether the toggle button is disabled. */
  @Input()
  get disabled(): boolean {
    if (this._disabled === undefined && this.datepicker) {
      return (this.datepicker as any).disabled
    }

    return !!this._disabled
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value)
  }
  private _disabled!: boolean

  /** Whether ripples on the toggle should be disabled. */
  @Input()
  disableRipple: boolean | undefined

  /** Underlying button element. */
  @ViewChild('button')
  _button: MatButton | undefined

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    @Attribute('tabindex') defaultTabIndex: string
  ) {
    const parsedTabIndex = Number(defaultTabIndex)
    this.tabIndex = parsedTabIndex || parsedTabIndex === 0 ? parsedTabIndex : null
  }

  ngOnChanges() {
    this._watchStateChanges()
  }

  ngOnDestroy() {
    this._stateChanges.unsubscribe()
  }

  ngAfterContentInit() {
    this._watchStateChanges()
  }

  _open(event: Event) {
    if (this.datepicker && !this.disabled) {
      this.datepicker.open()
      event.stopPropagation()
    }
  }

  private _watchStateChanges() {
    const datepickerStateChanged = this.datepicker ? this.datepicker.stateChanges : observableOf()
    const inputStateChanged = this.datepicker && this.datepicker.datepickerInput ? this.datepicker.datepickerInput.stateChanges : observableOf()
    const datepickerToggled = this.datepicker ? merge(this.datepicker.openedStream, this.datepicker.closedStream) : observableOf()

    this._stateChanges.unsubscribe()
    this._stateChanges = merge(datepickerStateChanged as Observable<void>, inputStateChanged, datepickerToggled).subscribe(() =>
      this._changeDetectorRef.markForCheck()
    )
  }
}
